import { Breadcrumbs, Drawer } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { capitalize } from 'lodash';
import { DateTime } from 'luxon';
import React, { useEffect, useState } from 'react';
import { connect, ConnectedProps, useSelector } from 'react-redux';
import { compose } from 'redux';
import { DeviceModeState, PersonIdNameTimezone } from '@redux/person/personTypes';
import { getDeviceModeByPersonIdSelector } from '@redux/person/personSelector';
import {
  getGeneratedMessageFromTaskMessageSelector,
  getGeneratedTaskNameFromTaskMessageSelector,
  getGeneratedTaskSummaryFromTaskMessageSelector,
  getPreviewAssessmentIdSelector,
  getSelectedAssessmentSelector
} from '@redux/tasks/tasksSelector';
import {
  clearGeneratedTaskMessageThunk,
  clearGeneratedTaskNameThunk,
  clearGeneratedTaskSummaryThunk,
  clearSelectedAssessmentThunk,
  getGeneratedMessageFromAssessmentMessageThunk,
  getGeneratedMessageFromTaskMessageThunk,
  setPreviewAssessmentIdThunk
} from '@redux/tasks/tasksThunks';
import { TaskCreationPayloadType, TaskTypesEnum } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { Button } from 'components/Button/Button';
import { CloseIconButton } from 'components/Button/CloseButton/CloseIconButton';
import { ConfirmationDialogV2 } from 'components/ConfirmationDialog/ConfirmationDialogV2';
import { ExpirySelectBlock } from 'components/ExpirySelectBlock/ExpirySelectBlock';
import { ENDS_SELECT_OPTIONS, FREQUENCY_SELECT_OPTIONS, FrequencySelectBlock, FrequencyType } from 'components/FrequencySelectBlock/FrequencySelectBlock';
import { AddUserIcon, BackArrowIcon, CircledCustomIcon, LeftChevronIcon, WarningCircleIcon } from 'components/Icons';
import { MessageReviewArea } from 'components/MessageReviewArea/MessageReviewArea';
import { BedroomTriggerTypes, LivingSpaceTriggerTypes, TriggerSelectBlock, TriggerTypes } from 'components/TriggerSelectBlock/TriggerSelectBlock';
import { Text } from 'components/Typography';
import { ConditionsPreview } from 'containers/Tasks/components/ConditionsPreview/ConditionsPreview';
import TaskPreviewArea from 'containers/Tasks/components/TaskPreviewArea/TaskPreviewArea';
import { Color } from 'utils/color';
import { isValidTaskDate, isValidTaskDateRecurrenceEnd, isValidTaskTime, validateTaskForm } from 'utils/form';
import { applyTimezoneKeepTimeOfDay, DEFAULT_TIMEZONE_ID, mergeDatePart, mergeTimePart } from 'utils/time';
import { customDateTimePickerStyles } from '../../../../customStyles';
import { ActivePersonSelect } from '../ActivePersonSelect/ActivePersonSelect';
import SelectedPatientDetails from '../SelectedPatientDetails/SelectedPatientDetails';
import TaskContentArea from '../TaskContentArea/TaskContentArea';
import style from './TaskCreationDrawer.scss';

enum TASK_CREATION_STAGES {
  CONDITIONS = 'conditions',
  TASK = 'task',
  REVIEW = 'review'
}

type TaskCreationConfirmationProps = {
  title: string;
  description: string;
  proceedText: string;
  closeText: string;
  proceedFunction: () => void;
};

type TaskCreationProps = {
  preselectedPerson?: string;
  //   task: TaskDataType;
  visible: boolean;
  onClose: () => void;
  activePersons: PersonIdNameTimezone[];
  completeSubmitNewTask: (task: TaskCreationPayloadType) => Promise<void>;
  onSuccessSubmit?: () => void;
};

type Props = ConnectedProps<typeof connectRedux> & TaskCreationProps;

const TaskCreationDrawer: React.FC<Props> = ({
  preselectedPerson,
  visible,
  onClose,
  activePersons,
  completeSubmitNewTask,
  createTaskMessage,
  createAssessmentMessage,
  clearGeneratedMessage,
  clearGeneratedName,
  clearGeneratedSummary,
  generatedTaskMessage = '',
  generatedTaskName = '',
  generatedTaskSummary = '',
  selectedAssessment,
  clearSelectedAssessment,
  previewAssessmentId,
  setPreviewAssessmentId,
  onSuccessSubmit
}) => {
  const getPatientTimezone = (patientId: String) => {
    return activePersons.find((value) => value.personId == patientId)?.timezone ?? DEFAULT_TIMEZONE_ID;
  };

  const createPlaceholderTime = (patientId: string): Date => {
    const patientTimezone = getPatientTimezone(patientId); // Assume this returns a valid IANA timezone string
    let patientNow = DateTime.now().setZone(patientTimezone);
    patientNow = patientNow.plus({ minutes: 5 - (patientNow.minute % 5) }).set({ second: 0, millisecond: 0 });

    const minHour = 5;
    const maxHour = 24;

    if (patientNow.hour < minHour) {
      patientNow = patientNow.set({ hour: minHour, minute: 0 });
    } else if (patientNow.hour >= maxHour) {
      patientNow = patientNow.plus({ days: 1 }).set({ hour: minHour, minute: 0 });
    }

    let localTime = patientNow.setZone('local', { keepLocalTime: true });
    const currentMinutes = localTime.minute;
    const newMinutes = currentMinutes + (15 - (currentMinutes % 15));
    localTime = localTime.set({ minute: newMinutes % 60, second: 0, millisecond: 0 });

    if (newMinutes >= 60) {
      localTime = localTime.plus({ hours: 1 });
    }

    return localTime.toJSDate();
  };

  const [creationStage, setCreationStage] = useState<TASK_CREATION_STAGES>(TASK_CREATION_STAGES.CONDITIONS);
  const [selectingPatient, setSelectingPatient] = useState<boolean>(false);
  const [taskType, setTaskType] = useState<TaskTypesEnum>(TaskTypesEnum.MESSAGE);
  const [message, setMessage] = useState<string>('');
  const [generatedMessage, setGeneratedMessage] = useState<Nullable<string>>(null);
  const [generatedName, setGeneratedName] = useState<Nullable<string>>(null);
  const [loadingMessage, setLoadingMessage] = useState<boolean>(false);
  const [patient, setPatient] = useState<string>('');
  const [dateTime, setDateTime] = useState<Date>(createPlaceholderTime(''));
  const [dateFormat, setDateFormat] = useState<Date | null>(null);
  const [timeFormat, setTimeFormat] = useState<Date | null>(null);
  const [expiredDateTime, setExpiredDateTime] = useState<Nullable<Date>>(null);
  const [frequency, setFrequency] = useState<FrequencyType>({ repeat: FREQUENCY_SELECT_OPTIONS.no_repeat });
  const [trigger, setTrigger] = useState<TriggerTypes>(TriggerTypes.inBed);
  const [confirmDialogProps, setConfirmDialogProps] = useState<Nullable<TaskCreationConfirmationProps>>(null);

  const deviceMode = useSelector((state: ReduxState) => (patient ? getDeviceModeByPersonIdSelector(state, patient) : undefined));

  useEffect(() => {
    if (deviceMode === DeviceModeState.LIVING_SPACE) {
      setTrigger(LivingSpaceTriggerTypes[0]);
    } else {
      setTrigger(BedroomTriggerTypes[0]);
    }
  }, [deviceMode]);

  const clearAllMessages = () => {
    setMessage('');
    setGeneratedMessage(null);
    setGeneratedName(null);
    clearGeneratedName();
    clearGeneratedMessage();
    clearGeneratedSummary();
  };

  useEffect(() => {
    //clear task messages and name when the selected tab is changed
    clearAllMessages();
    clearSelectedAssessment();
  }, [taskType]);

  useEffect(() => {
    //clear task messages and name when the selected assessment is changed
    clearAllMessages();
  }, [selectedAssessment]);

  useEffect(() => {
    if (preselectedPerson && visible) {
      setPatient(preselectedPerson);
    }
  }, [preselectedPerson, visible]);

  useEffect(() => {
    setGeneratedMessage(generatedTaskMessage);
  }, [generatedTaskMessage]);

  useEffect(() => {
    setGeneratedName(generatedTaskName);
  }, [generatedTaskName]);

  useEffect(() => {
    const newErrors = validateForm();

    const requiredFieldsByStage: Record<string, (keyof typeof newErrors)[]> = {
      [TASK_CREATION_STAGES.CONDITIONS]: ['patient', 'dateTime', 'dateFormat', 'timeFormat', 'trigger', 'expiredDateTime', 'frequencyAfter', 'frequencyDate'],
      [TASK_CREATION_STAGES.TASK]: ['message'],
      [TASK_CREATION_STAGES.REVIEW]: []
    };

    const requiredFields = requiredFieldsByStage[creationStage] || [];
    const canContinue = requiredFields.every((field) => newErrors[field] === '');

    setContinueButtonDisabled(!canContinue);
  }, [message, generatedMessage, generatedName, patient, dateTime, dateFormat, timeFormat, frequency, creationStage, trigger, expiredDateTime]);

  const [errors, setErrors] = useState({
    message: '',
    patient: '',
    dateTime: '',
    dateFormat: '',
    timeFormat: '',
    frequencyAfter: '',
    frequencyDate: '',
    trigger: '',
    expiredDateTime: ''
  });
  const [continueButtonDisabled, setContinueButtonDisabled] = useState<boolean>(false);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState<boolean>(false);

  const createMessage = async () => {
    let assigneeName = activePersons.find((value) => value.personId == patient)?.fullName;
    let convertedTime = applyTimezoneKeepTimeOfDay(dateTime, getPatientTimezone(patient));
    setLoadingMessage(true);

    if (taskType === TaskTypesEnum.MESSAGE) {
      createTaskMessage({
        taskMessage: message,
        assigneeId: patient,
        assigneeFullName: assigneeName || '',
        scheduledForDateTime: convertedTime?.toISOString()
      }).then(() => {
        setLoadingMessage(false);
      });
    }
    if (taskType === TaskTypesEnum.ASSESSMENT) {
      createAssessmentMessage({
        personFullName: assigneeName || '',
        assessmentName: selectedAssessment?.name || '',
        scheduledDateTime: convertedTime?.toISOString()
      }).then(() => {
        setLoadingMessage(false);
      });
    }
  };

  const progressStage = () => {
    if (creationStage === TASK_CREATION_STAGES.CONDITIONS) {
      setCreationStage(TASK_CREATION_STAGES.TASK);
    } else if (creationStage === TASK_CREATION_STAGES.TASK) {
      if (!generatedName) {
        setGeneratedName(message.slice(0, 30));
      }
      setCreationStage(TASK_CREATION_STAGES.REVIEW);
    } else if (creationStage === TASK_CREATION_STAGES.REVIEW) {
      submitNewTask();
    }
  };

  const regressStage = () => {
    if (creationStage === TASK_CREATION_STAGES.CONDITIONS) {
    } else if (creationStage === TASK_CREATION_STAGES.TASK) {
      //TODO clear task variables
      setCreationStage(TASK_CREATION_STAGES.CONDITIONS);
    } else if (creationStage === TASK_CREATION_STAGES.REVIEW) {
      setCreationStage(TASK_CREATION_STAGES.TASK);
    }
  };

  const submitNewTask = () => {
    if (Object.values(errors).every((error) => error === '')) {
      setSubmitButtonDisabled(true);
      let assigneeName = activePersons.find((value) => value.personId == patient)?.fullName;
      let convertedTime = applyTimezoneKeepTimeOfDay(dateTime, getPatientTimezone(patient));
      let convertedExpiredTime = applyTimezoneKeepTimeOfDay(expiredDateTime!, getPatientTimezone(patient));
      completeSubmitNewTask({
        taskType,
        taskMessage: capitalize(message.trim()),
        assigneeId: patient,
        assigneeFullName: assigneeName ?? '',
        scheduledForDateTime: convertedTime?.toISOString(),
        generatedMessage: capitalize(message.trim()),
        taskName: selectedAssessment?.name ?? capitalize(generatedName?.trim()) ?? '',
        frequency: frequency.repeat,
        frequencyEnds: frequency.repeat !== FREQUENCY_SELECT_OPTIONS.no_repeat ? frequency.ends : null,
        endsAfterOccurrences: frequency.ends === ENDS_SELECT_OPTIONS.after ? frequency.after : null,
        endsOnDate: frequency.ends === ENDS_SELECT_OPTIONS.date ? DateTime.fromJSDate(frequency.date!).toISODate() : null,
        triggerCondition: trigger,
        expiredForDateTime: convertedExpiredTime?.toISOString(),
        taskSummary: capitalize(generatedTaskSummary?.trim()) || undefined,
        ...(taskType === TaskTypesEnum.ASSESSMENT ? { assessmentId: selectedAssessment?.id as string } : {})
      })
        .then(() => {
          clearGeneratedName();
          clearGeneratedMessage();
          clearGeneratedSummary();
          clearSelectedAssessment();
        })
        .then(() => onSuccessSubmit?.())
        .finally(() => setSubmitButtonDisabled(false));
    }
  };

  const validateForm = () => {
    const newErrors = validateTaskForm(
      { message, generatedMessage, generatedName, patient, dateTime, dateFormat, timeFormat, frequency, trigger, expiredDateTime },
      getPatientTimezone(patient)
    );
    setErrors(newErrors);

    return newErrors;
  };

  const patientSelectionChanged = (patientId: string) => {
    setPatient(patientId);
    setDateTime(createPlaceholderTime(patientId));
  };

  const closeDrawer = () => {
    clearGeneratedName();
    clearGeneratedMessage();
    clearGeneratedSummary();
    clearSelectedAssessment();
    onClose();
  };

  const clearTaskCreation = () => {
    setCreationStage(TASK_CREATION_STAGES.CONDITIONS);
    setSelectingPatient(false);
    setPatient('');
    setTaskType(TaskTypesEnum.MESSAGE);
    setDateTime(createPlaceholderTime(''));
    setMessage('');
    setGeneratedMessage(null);
    setLoadingMessage(false);

    setConfirmDialogProps(null);
    closeDrawer();
  };

  const clearPatient = () => {
    setPatient('');
    setSelectingPatient(true);
    setMessage('');
    setGeneratedMessage(null);
    setCreationStage(TASK_CREATION_STAGES.CONDITIONS);
    setConfirmDialogProps(null);
  };

  const showConfirmCloseDrawer = () => {
    setConfirmDialogProps({
      title: 'You have unsaved changes',
      description: 'If you close the task now, the changes will be lost. Do you wish to exit?',
      proceedText: 'Yes, exit',
      closeText: 'Go back',
      proceedFunction: clearTaskCreation
    });
  };

  const showConfirmClearPatient = () => {
    if (generatedMessage || message) {
      setConfirmDialogProps({
        title: 'Switching patients will reset your task',
        description: 'You can`t reuse the generated message for another patient. If you switch, you`ll need to start creating the task message from scratch.',
        proceedText: 'I understand',
        closeText: 'Cancel',
        proceedFunction: clearPatient
      });
    } else {
      setPatient('');
      setSelectingPatient(true);
      setCreationStage(TASK_CREATION_STAGES.CONDITIONS);
    }
  };

  return (
    <Drawer anchor="right" open={visible} onClose={closeDrawer} variant="temporary">
      <div className={style.mainContainer}>
        <div className={style.drawerContainer}>
          <div className={style.patientContainer}>
            {patient ? (
              <SelectedPatientDetails
                patientId={patient}
                patientTimeZone={getPatientTimezone(patient)}
                clearSelectedPatient={showConfirmClearPatient}
                hasPreselectedPatient={!!preselectedPerson}
              />
            ) : (
              <>
                {selectingPatient ? (
                  <>
                    <div className={style.selectPatientHeaderContainer}>
                      <Button
                        variant="text"
                        startIcon={<BackArrowIcon color={Color.DARK_BLUE_NEW} />}
                        className={style.backArrowButton}
                        onClick={() => setSelectingPatient(false)}
                      ></Button>
                      <Text variant="2xl" weight={500}>
                        Select patient
                      </Text>
                    </div>
                    <ActivePersonSelect error={Boolean(errors.patient)} activePersons={activePersons} patientSelectionChanged={patientSelectionChanged}></ActivePersonSelect>
                  </>
                ) : (
                  <>
                    <div data-testid="select-patient-button" className={style.addPatientContainer} onClick={() => setSelectingPatient(true)}>
                      <CircledCustomIcon icon={AddUserIcon} containerStyle={style.circleIcon} />
                      <Text variant="xl" weight={500} className={style.addPatientTitle}>
                        Add patient
                      </Text>
                      <Text variant="md" weight={400} className={style.addPatientInfo}>
                        Choose an existing patient or add a new one to start creating tasks.
                      </Text>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className={style.taskContainer}>
            <div className={style.drawerHeader}>
              <Breadcrumbs className={style.breadcrumbs}>
                <Text className={creationStage === TASK_CREATION_STAGES.CONDITIONS ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Conditions</Text>
                <Text className={creationStage === TASK_CREATION_STAGES.TASK ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Task</Text>
                <Text className={creationStage === TASK_CREATION_STAGES.REVIEW ? style.activeBreadcrumb : style.inactiveBreadcrumb}>Review</Text>
              </Breadcrumbs>

              <CloseIconButton containerClassName={style.closeButton} onClick={showConfirmCloseDrawer} />
            </div>

            <div className={style.taskConfigurationContainer}>
              {creationStage == TASK_CREATION_STAGES.CONDITIONS && (
                <>
                  <Text variant="h5" weight={500} className={style.title}>
                    Task conditions
                  </Text>

                  <div className={style.multiConditionLine}>
                    <div className={style.conditionHolder}>
                      <Text className={style.conditionTitle} variant="nlg">
                        Date
                      </Text>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={dateTime}
                          className={style.datePicker}
                          onChange={(next) =>
                            setDateTime((prev) => {
                              //mergeDatePart allows update only date part of the dateTime value
                              const result = mergeDatePart(prev, next);
                              //setDateFormat and setTimeFormat are used only for the date/time formats validation purposes
                              setDateFormat(next);
                              setTimeFormat(mergeTimePart(prev, next));
                              return result ?? prev;
                            })
                          }
                          disabled={!patient}
                          shouldDisableDate={(date) => !isValidTaskDate(date as Date)}
                          sx={customDateTimePickerStyles}
                        />
                      </LocalizationProvider>
                      {!!errors.dateFormat && (
                        <Text variant="sm" className={style.error}>
                          {errors.dateFormat}
                        </Text>
                      )}
                    </div>
                  </div>
                  <div className={style.multiConditionLine}>
                    <div className={style.conditionHolder}>
                      <Text variant="nlg" className={style.conditionTitle}>
                        Deliver at
                      </Text>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <TimePicker
                          value={dateTime}
                          className={style.timePicker}
                          onChange={(next) =>
                            setDateTime((prev) => {
                              //mergeTimePart allows update only time part of the dateTime value
                              const result = mergeTimePart(prev, next);
                              //setDateFormat and setTimeFormat are used only for the date/time formats validation purposes
                              setTimeFormat(next);
                              setDateFormat(mergeDatePart(prev, next));
                              return result ?? prev;
                            })
                          }
                          disabled={!patient}
                          shouldDisableTime={(timeValue, clockType) => {
                            const candidate = new Date(dateTime);
                            if (clockType === 'hours') {
                              candidate.setHours(timeValue.getHours());
                            } else if (clockType === 'minutes') {
                              candidate.setMinutes(timeValue.getMinutes());
                            }
                            return !isValidTaskTime(candidate, getPatientTimezone(patient));
                          }}
                          sx={customDateTimePickerStyles}
                        />
                      </LocalizationProvider>
                      {!!errors.timeFormat && (
                        <Text variant="sm" className={style.error}>
                          {errors.timeFormat}
                        </Text>
                      )}
                      {!errors.timeFormat && !!errors.dateTime && (
                        <Text variant="sm" className={style.error}>
                          {errors.dateTime}
                        </Text>
                      )}
                    </div>
                    <div className={style.conditionHolder}>
                      <ExpirySelectBlock
                        selectedTime={dateTime}
                        expiredTime={expiredDateTime}
                        onExpiredTimeSelected={setExpiredDateTime}
                        disabled={!patient}
                        error={errors.expiredDateTime}
                      />
                    </div>
                  </div>
                  <TriggerSelectBlock trigger={trigger} onTriggerChanged={setTrigger} deviceMode={deviceMode} error={errors.trigger} />
                  <FrequencySelectBlock
                    frequency={frequency}
                    onFrequencyChanged={setFrequency}
                    filterDate={(date: Date) => isValidTaskDateRecurrenceEnd(date, dateTime)}
                    errors={{ after: errors.frequencyAfter, date: errors.frequencyDate }}
                  />
                </>
              )}
              {creationStage == TASK_CREATION_STAGES.TASK && (
                <>
                  <div>
                    <Text variant="h5" weight={500}>
                      Create task
                    </Text>
                    <ConditionsPreview frequency={frequency} trigger={trigger} selectedDateTime={dateTime} expiredDateTime={expiredDateTime!} />
                  </div>
                  <TaskContentArea
                    taskType={taskType}
                    setTaskType={setTaskType}
                    message={message}
                    setMessage={setMessage}
                    loadingMessage={loadingMessage}
                    generatedMessage={generatedMessage}
                    createMessage={createMessage}
                    clearGeneratedMessage={clearGeneratedMessage}
                    editGeneratedMessage={(newMessage: string) => setGeneratedMessage(newMessage)}
                  />
                </>
              )}
              {creationStage == TASK_CREATION_STAGES.REVIEW && (
                <>
                  <div>
                    <Text variant="h5" weight={500}>
                      {selectedAssessment ? `${selectedAssessment.name} assessment` : generatedName}
                    </Text>
                    <ConditionsPreview frequency={frequency} trigger={trigger} selectedDateTime={dateTime} expiredDateTime={expiredDateTime!} />
                  </div>
                  <MessageReviewArea message={message!} assessment={selectedAssessment} />
                </>
              )}
            </div>

            <div className={style.drawerFooter}>
              {creationStage != TASK_CREATION_STAGES.CONDITIONS && (
                <Button variant="outlined" onClick={regressStage} startIcon={<LeftChevronIcon height={16} width={16} />}>
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                onClick={progressStage}
                className={style.continueButton}
                disabled={creationStage == TASK_CREATION_STAGES.REVIEW ? submitButtonDisabled : continueButtonDisabled}
              >
                {creationStage == TASK_CREATION_STAGES.REVIEW ? 'Submit' : 'Continue'}
              </Button>
            </div>
          </div>
          {previewAssessmentId && <TaskPreviewArea onClose={showConfirmCloseDrawer} onBack={() => setPreviewAssessmentId(null)} />}
        </div>
      </div>
      <ConfirmationDialogV2
        open={!!confirmDialogProps}
        icon={WarningCircleIcon}
        title={confirmDialogProps?.title}
        description={confirmDialogProps?.description}
        proceedEvent={() => confirmDialogProps?.proceedFunction()}
        proceedEventText={confirmDialogProps?.proceedText}
        closeEvent={() => {
          setConfirmDialogProps(null);
        }}
        closeEventText={confirmDialogProps?.closeText}
      />
    </Drawer>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    generatedTaskMessage: getGeneratedMessageFromTaskMessageSelector(state),
    generatedTaskName: getGeneratedTaskNameFromTaskMessageSelector(state),
    generatedTaskSummary: getGeneratedTaskSummaryFromTaskMessageSelector(state),
    selectedAssessment: getSelectedAssessmentSelector(state),
    previewAssessmentId: getPreviewAssessmentIdSelector(state)
  }),
  {
    setPreviewAssessmentId: setPreviewAssessmentIdThunk,
    createTaskMessage: getGeneratedMessageFromTaskMessageThunk,
    createAssessmentMessage: getGeneratedMessageFromAssessmentMessageThunk,
    clearGeneratedMessage: clearGeneratedTaskMessageThunk,
    clearGeneratedName: clearGeneratedTaskNameThunk,
    clearGeneratedSummary: clearGeneratedTaskSummaryThunk,
    clearSelectedAssessment: clearSelectedAssessmentThunk
  }
);

export { TaskCreationDrawer };
export default compose(connectRedux)(TaskCreationDrawer);
